import * as React from "react";
import { storageKeys } from "@/lib/api/tokens";
import { fitToSurface, normaliseHex, readableInk, withAlpha } from "@/lib/color";

export type ColorScheme = "dark" | "light" | "system";
type Resolved = "dark" | "light";

interface ThemeContextValue {
  scheme: ColorScheme;
  resolved: Resolved;
  setScheme: (scheme: ColorScheme) => void;
  /** Applied by the business provider once the active business is known. */
  setBrandColor: (hex: string | null) => void;
  brandColor: string | null;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const DARK_SURFACE = "#05070f";
const LIGHT_SURFACE = "#f7f8fb";

function readScheme(): ColorScheme {
  try {
    const stored = localStorage.getItem(storageKeys.colorScheme);
    if (stored === "dark" || stored === "light" || stored === "system") return stored;
  } catch {
    /* ignore */
  }
  return "dark";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [scheme, setSchemeState] = React.useState<ColorScheme>(readScheme);
  const [brandColor, setBrandColor] = React.useState<string | null>(null);
  const [systemDark, setSystemDark] = React.useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved: Resolved = scheme === "system" ? (systemDark ? "dark" : "light") : scheme;

  React.useEffect(() => {
    const root = document.documentElement;
    // Stamp both ways round: dark is the CSS default on :root, so light has to
    // be asserted explicitly rather than inferred from the absence of .dark.
    root.classList.toggle("dark", resolved === "dark");
    root.classList.toggle("light", resolved === "light");
    root.style.colorScheme = resolved;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", resolved === "dark" ? DARK_SURFACE : LIGHT_SURFACE);
  }, [resolved]);

  const setScheme = React.useCallback((next: ColorScheme) => {
    setSchemeState(next);
    try {
      localStorage.setItem(storageKeys.colorScheme, next);
    } catch {
      /* ignore */
    }
  }, []);

  // A custom brand colour overrides the primary/ring tokens. It is fitted to the
  // active surface first: a logo-derived colour is often too dark or too pale to
  // sit on the page at 3:1, and a brand colour nobody can read is worse than ours.
  React.useEffect(() => {
    const root = document.documentElement;
    const vars = ["--primary", "--primary-foreground", "--ring", "--brand", "--brand-glow"];
    const hex = normaliseHex(brandColor);
    if (!hex) {
      for (const v of vars) root.style.removeProperty(v);
      return;
    }
    const surface = resolved === "dark" ? DARK_SURFACE : LIGHT_SURFACE;
    const fitted = fitToSurface(hex, surface);
    root.style.setProperty("--primary", fitted);
    root.style.setProperty("--primary-foreground", readableInk(fitted));
    root.style.setProperty("--ring", fitted);
    root.style.setProperty("--brand", fitted);
    root.style.setProperty("--brand-glow", withAlpha(fitted, resolved === "dark" ? 0.24 : 0.18));
    return () => {
      for (const v of vars) root.style.removeProperty(v);
    };
  }, [brandColor, resolved]);

  const value = React.useMemo(
    () => ({ scheme, resolved, setScheme, setBrandColor, brandColor }),
    [scheme, resolved, setScheme, brandColor],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useColorScheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useColorScheme must be used inside <ThemeProvider>");
  return ctx;
}
